import {CarePlanConfigurationFeature} from '../../../../../backend/enums/care-plan-configuration-feature';
import {
  ExportCarePlanTableColumnStyle,
  ExportCarePlanTableStylesOpts,
} from '../base/export-care-plan-types';
import {
  RowData,
  ExportCarePlanGroupDataProcessor,
} from '../base/export-care-plan-group-data-processor';
import {translations} from '../../../../../i18n/translations';
import moment from 'moment';
import {PosologyScheduleFormType} from '../../../../../types';
import {safeParseJson} from '../../../../functions/safe-parse-json';
import {scheduleReadableExpression} from '../../../../functions/schedule-readable-expression';

export class CommonBadlPdfDataProcessor extends ExportCarePlanGroupDataProcessor {
  handleTaskDetailsCellValue: ((rowData: RowData) => string) | undefined = undefined;

  override resolveDetailsHeaders(firstRowData: RowData): string[] {
    let obsHeader = '';
    let headers: string[] = [];

    for (const entry of firstRowData.entries) {
      if (entry.feature === CarePlanConfigurationFeature.Notes) {
        obsHeader = entry.name;
      } else {
        headers.push(entry.name);
      }
    }

    return [...headers, obsHeader];
  }

  override resolveDetailsRow(rowData: RowData): string[] {
    const cells: string[] = [];
    let obsCell = '';

    for (const entry of rowData.entries) {
      if (entry.feature === CarePlanConfigurationFeature.Notes) {
        obsCell = this.getEntryValue(entry);
      } else {
        cells.push(this.resolveEntryCell(entry));
      }
    }

    return [...cells, obsCell];
  }

  override resolveDetailsColumnStyles(
    detailsHeaders: string[]
  ): ExportCarePlanTableColumnStyle | undefined {
    return this.createColumnStyles(detailsHeaders, 45);
  }

  override async resolveTaskHeaders(firstRowData: RowData): Promise<string[]> {
    // 1. start-date
    const startHeader = this.t(translations.time_lapse.start_text);

    // 2. end-date (from schedule.endDate)
    const endHeader = this.t(translations.time_lapse.end_text);

    // 3. task
    const taskHeader = this.t(translations.export.task);

    // 4. details
    const detailsHeader = this.t(translations.export.details);

    // 5. periodicity (schedule readable expression)
    const periodicityHeader = this.t(translations.export.periodicity);

    // 6. Obs
    const obsHeader = this.t(translations.export.observations);

    if (this.handleTaskDetailsCellValue) {
      return [startHeader, endHeader, taskHeader, detailsHeader, periodicityHeader, obsHeader];
    } else {
      return [startHeader, endHeader, taskHeader, periodicityHeader, obsHeader];
    }
  }

  override resolveTaskRow(rowData: RowData) {
    const result: string[] = [];

    // 1. start-date
    const startDate = rowData.getEntryByFeature(CarePlanConfigurationFeature.StartsAtTime)?.value;
    result.push(startDate ? moment(startDate).format('DD/MM/YYYY') : '');

    // 2. end-date (from schedule.endDate)
    const schedule: PosologyScheduleFormType | undefined = safeParseJson(
      rowData.getEntryByFeature(CarePlanConfigurationFeature.Schedule)?.value
    );
    result.push(schedule?.endDate ? moment(schedule.endDate).format('DD/MM/YYYY') : '');

    // 3. task
    const task = rowData.getEntryByFeature(CarePlanConfigurationFeature.Name);
    result.push(this.getEntryValue(task));

    // 4. details
    if (this.handleTaskDetailsCellValue) {
      const details = this.handleTaskDetailsCellValue(rowData);
      result.push(details);
    }

    // 5. periodicity (schedule readable expression)
    const dateExpression = schedule
      ? scheduleReadableExpression({t: this.t, scheduleDefinition: schedule})
      : undefined;
    result.push(dateExpression ?? this.t(translations.export.no_repetition));

    // 6. Obs
    const obs = rowData.getEntryByFeature(CarePlanConfigurationFeature.Notes);
    result.push(this.getEntryValue(obs));

    return result;
  }

  override resolveTasksColumnStyles(
    taskHeaders: string[]
  ): ExportCarePlanTableColumnStyle | undefined {
    if (this.handleTaskDetailsCellValue) {
      return {
        // start-date
        0: {cellWidth: 20},
        // end-date
        1: {cellWidth: 20},
        // task
        2: {cellWidth: 40},
        // details
        3: {cellWidth: 60},
        // periodicity
        4: {cellWidth: 50},
      };
    }

    return {
      // start-date
      0: {cellWidth: 20},
      // end-date
      1: {cellWidth: 20},
      // task
      2: {cellWidth: 40},
      // periodicity
      3: {cellWidth: 50},
    };
  }

  protected createColumnStyles(
    headers: string[],
    defaultCellWidth: number
  ): ExportCarePlanTableColumnStyle {
    const columnStyles = headers.map<[number, ExportCarePlanTableStylesOpts]>((_, idx) => [
      idx,
      // last column is set to auto.
      {cellWidth: idx === headers.length - 1 ? 'auto' : defaultCellWidth},
    ]);

    return Object.fromEntries(columnStyles);
  }
}
